import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import LimitIndicator from './LimitIndicator';
import NoLimitIndicator from './NoLimitIndicator';
import banner from '@assets/asas-modal.jpg';
import { formatPhone } from '@utils/formatPhone';
import logoAllseg from '@assets/allseg-seguradora-azul.svg';
import logoAsasGarantia from '@assets/asas-garantia.svg';
import { useNavigate } from '@tanstack/react-router';
import { logger } from '@/tracer';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { isValidCNPJ } from '@utils/isValidCNPJ';
import { generateFlowId, persistFlowId, ORIGIN_ID } from '@utils/flowId';

interface ApiLimite {
  idSeguradora: number;
  limiteCredito: number;
  seguradora: string;
}

interface ApiMsg {
  idSeguradora: number;
  message: string;
  businessMessage: string;
  termoAceite: boolean;
}

interface ApiResponse {
  idTomador: number;
  idCorretor: number;
  limites: ApiLimite[];
  msgs: ApiMsg[];
}

interface RedirectData {
  telefone: string;
  cnpj: string;
  email: string;
  name: string;
  limiteCredito: number;
  idCorretor: number;
  idTomador: number;
  idModalidade: number;
  termoAceite: boolean;
}

interface AsasFormModalProps {
  open: boolean;
  onClose: () => void;
}

export const AsasFormModal = ({ open, onClose }: AsasFormModalProps) => {
  const [form, setForm] = useState({
    telefone: '',
    cnpj: '',
    email: '',
    nome: '',
    id: 245,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [noLimit, setNoLimit] = useState(false);
  const [limiteCredito, setLimiteCredito] = useState(0);
  const [redirectData, setRedirectData] = useState<RedirectData | null>(null);
  const [timeoutProgress, setTimeoutProgress] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const didNavigateRef = useRef(false);
  const flowIdRef = useRef<string>('');

  const navigate = useNavigate();

  function startTimeoutProgress(duration, onFinish?: () => void) {
    const start = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min((elapsed / duration) * 100, 100);

      setTimeoutProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        onFinish?.();
      }
    }, 100);

    return () => clearInterval(interval);
  }

  const resetState = () => {
    setForm({ telefone: '', cnpj: '', email: '', nome: '', id: 245 });
    setError('');
    setLoading(false);
    setNoLimit(false);
    setLimiteCredito(0);
    setRedirectData(null);
    try {
      localStorage.removeItem('asasAccessToken');
    } catch (e) {
      // ignore if localStorage not available in test env
    }
  };

  useEffect(() => {
    if (!open) return;
    const flowId = generateFlowId();
    flowIdRef.current = flowId;
    logger.emit({
      severityNumber: SeverityNumber.INFO,
      severityText: 'INFO',
      body: 'ui.modal.opened',
      attributes: { 'flow.id': flowId, 'origin.id': ORIGIN_ID },
    });
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const formatCNPJ = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,4})/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 14) {
      setForm({ ...form, cnpj: value });
    }
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length <= 11) {
      setForm({ ...form, telefone: digits });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

      const flowId = flowIdRef.current;
      const cadastralAttrs = {
        'user.cnpj': form.cnpj,
        'user.nome': form.nome,
        'user.email': form.email,
        'user.telefone': form.telefone,
        'corretor.id': form.id,
      };

      logger.emit({
        severityNumber: SeverityNumber.INFO,
        severityText: 'INFO',
        body: 'api.consult.requested',
        attributes: {
          ...cadastralAttrs,
          'flow.id': flowId,
          'origin.id': ORIGIN_ID,
        },
      });

      setError('');
      setLoading(true);
      setShowProgress(true);
      setTimeoutProgress(0);

      if (form.cnpj.length < 14) {
        setError('Por favor, insira um CNPJ válido com 14 dígitos.');
        setLoading(false);
        return;
      }

      if (!isValidCNPJ(form.cnpj)) {
        setError('Por favor, insira um CNPJ válido.');
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      const stopProgress = startTimeoutProgress(180000, () => {
        setTimedOut(true);
        setError(
          'Tempo de espera excedido, por favor entre em contato com o suporte@exemplo.com',
        );
        setShowProgress(false);
        controller.abort();
      });

      try {
        /* ---------------- TOKEN ---------------- */

        const tokenResponse = await fetch(
          `${process.env.API_ASAS_GARANTIA_URL}/services/garantia-security-api/oauth/token`,
          {
            method: 'POST',
            headers: {
              Authorization:
                'Basic Z2FyYW50aWFzLXdlYi1hcHA6MXEydzNlNHIjZ2FyYW50aWFz',
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: 'password',
              username: 'integracao.bbmnet@asasinsurance.com',
              password: '{G5QL!(Avsrf',
            }),
          },
        );

        if (!tokenResponse.ok) {
          throw new Error(`Login falhou (${tokenResponse.status})`);
        }

        const { access_token: accessToken } = await tokenResponse.json();
        localStorage.setItem('asasAccessToken', accessToken);

        /* ---------------- CONSULTA ---------------- */

        const consultaResponse = await fetch(
          `${process.env.API_ASAS_GARANTIA_URL}/services/garantia-api/licitacoes/consultaTomador`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              telefone: form.telefone,
              cnpjConsulta: form.cnpj,
              email: form.email,
              idCorretor: form.id,
              name: form.nome,
            }),
            signal: controller.signal,
          },
        );

        const json = (await consultaResponse.json()) as ApiResponse;

        if (!consultaResponse.ok) {
          if (timedOut) return;
          throw new Error(`Consulta falhou (${consultaResponse.status})`);
        }

        const firstMsg = json.msgs?.[0];
        const firstLimite = json.limites?.[0];

        if (firstMsg && firstMsg.businessMessage !== 'Sucesso.') {
          logger.emit({
            severityNumber: SeverityNumber.WARN,
            severityText: 'WARN',
            body: 'api.consult.business_message',
            attributes: {
              ...cadastralAttrs,
              'flow.id': flowId,
              'origin.id': ORIGIN_ID,
              'tomador.id': json.idTomador,
              'credito.valor': firstLimite?.limiteCredito ?? 0,
              'business.message': firstMsg.businessMessage,
            },
          });
          setError(firstMsg.businessMessage);
          return;
        }

        if (!firstLimite || firstLimite.limiteCredito == null) {
          setError(
            'Não foi possível verificar o crédito. Entre em contato com a ASAS.',
          );
          return;
        }

        if (firstLimite.limiteCredito <= 0) {
          logger.emit({
            severityNumber: SeverityNumber.WARN,
            severityText: 'WARN',
            body: 'api.consult.business_message',
            attributes: {
              ...cadastralAttrs,
              'flow.id': flowId,
              'origin.id': ORIGIN_ID,
              'tomador.id': json.idTomador ?? null,
              'credito.valor': 0,
            },
          });
          setNoLimit(true);
        } else {
          logger.emit({
            severityNumber: SeverityNumber.INFO,
            severityText: 'INFO',
            body: 'api.consult.success',
            attributes: {
              ...cadastralAttrs,
              'flow.id': flowId,
              'origin.id': ORIGIN_ID,
              'tomador.id': json.idTomador,
              'credito.valor': firstLimite.limiteCredito,
            },
          });
          setLimiteCredito(firstLimite.limiteCredito);
          setRedirectData({
            telefone: form.telefone,
            cnpj: form.cnpj,
            email: form.email,
            limiteCredito: firstLimite.limiteCredito,
            idCorretor: 245,
            idTomador: json.idTomador,
            idModalidade: 17,
            termoAceite: firstMsg?.termoAceite ?? false,
          });
        }
      } catch (err: any) {
        logger.emit({
          severityNumber: SeverityNumber.ERROR,
          severityText: 'ERROR',
          body: 'api.consult.failed',
          attributes: {
              ...cadastralAttrs,
              'flow.id': flowId,
              'origin.id': ORIGIN_ID,
              message: err?.message,
              name: err?.name
            },
        });

        if (err.name === 'AbortError') {
          setError(
            'Tempo de espera excedido, por favor entre em contato com o suporte@exemplo.com',
          );
        } else {
          setError(err?.message || 'Ocorreu um erro inesperado.');
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
        setTimeoutProgress(0);
        stopProgress();
        setShowProgress(false);
      }
  };



  const handleConsultClick = () => {
    if (redirectData) {
      didNavigateRef.current = true;
      localStorage.setItem('consultaCredito', JSON.stringify(redirectData));
      navigate({ to: '/editais/contratacao' });
    }
  };

  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    const flowId = flowIdRef.current;

    if (!wasOpen || open) return; // só dispara na transição open=true → open=false

    const consultaResultado = noLimit
      ? 'sem_limite'
      : redirectData && didNavigateRef.current
        ? 'convertido'
        : redirectData
          ? 'somente_consultou'
          : 'abandonado';

    logger.emit({
      severityNumber: SeverityNumber.INFO,
      severityText: 'INFO',
      body: 'ui.modal.closed',
      attributes: {
        'consulta.resultado': consultaResultado,
        hadError: Boolean(error),
        step: noLimit ? 'no_limit' : redirectData ? 'success' : 'initial',
        'flow.id': flowId,
        'origin.id': ORIGIN_ID,
      },
    });

    didNavigateRef.current = false;
  }, [open, error, noLimit, redirectData]);


  const handleDialogClose = useCallback(
    (_: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      logger.emit({
        severityNumber: SeverityNumber.INFO,
        severityText: 'INFO',
        body: 'ui.modal.close_attempt',
        attributes: { reason, loading: String(loading) },
      });

      if (
        loading &&
        (reason === 'backdropClick' || reason === 'escapeKeyDown')
      ) {
        return;
      }

      resetState();
      onClose();
    },
    [loading, onClose],
  );


  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={loading}
      maxWidth={false}
      sx={{ maxWidth: 1170, mx: 'auto' }}
      PaperProps={{
        sx: {
          borderRadius: '13px',
          maxHeight: '100dvh',
          m: { xs: 0, md: 2 },
          width: { xs: '100%', md: 'auto' },
          overflowY: 'auto',
        },
      }}
    >
      <DialogContent sx={{ p: 0, background: '#fff' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: { xs: '18vh', md: '223px' },
              minHeight: { xs: '100px', md: '223px' },
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              boxShadow: '0px 4px 4px 0px #00000040',
              borderRadius: '13px',
            }}
          >
            <img
              src={banner}
              alt="Pessoa digitando em computador"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderEndEndRadius: 12,
              }}
            />
            {/* Banner */}
            <Box
              sx={{
                position: 'absolute',
                left: { xs: '20px', md: '53px' },
                top: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                height: '100%',
                gap: { xs: '14px', md: '66px' },
              }}
            >
              <Box
                component="img"
                src={logoAsasGarantia}
                alt="ASAS Garantias"
                sx={{
                  height: { xs: '32px', md: '77px' },
                  width: { xs: '104px', md: '249px' },
                }}
              />
              <Box
                component="img"
                src={logoAllseg}
                alt="AllSeg Seguradora"
                sx={{
                  height: { xs: '39px', md: '94px' },
                  width: { xs: '78px', md: '184px' },
                }}
              />
            </Box>
            <IconButton
              onClick={() => {
                resetState();
                onClose();
              }}
              sx={{
                position: 'absolute',
                top: { xs: '8px', md: '12px' },
                right: { xs: '8px', md: '12px' },
                color: '#fff',
              }}
              disabled={loading}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </Box>
          {/* Content */}
          {limiteCredito > 0 ? (
            <LimitIndicator
              limit={limiteCredito}
              onConsult={handleConsultClick}
            />
          ) : noLimit ? (
            <NoLimitIndicator />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                px: { xs: '16px', md: '54px' },
                pt: { xs: '12px', md: '36px' },
                pb: { xs: '16px', md: '56px' },
                gap: '20px',
              }}
            >
              <Box sx={{ flex: 2 }}>
                <Typography
                  color="primary"
                  gutterBottom
                  sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#004AA0',
                    maxWidth: { md: '40ch' },
                  }}
                >
                  Quer um seguro Garantia para participar da licitação?
                  <span style={{ fontWeight: 400 }}> Contrate aqui.</span>
                </Typography>
                <Typography
                  gutterBottom
                  sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 400,
                    color: '#808080',
                  }}
                >
                  A BBMNET Licitações, em parceria com a Asas Garantias e a
                  AllSeg Seguradora, traz mais segurança e agilidade para quem
                  participa de licitações públicas.
                </Typography>
                <Typography
                  gutterBottom
                  sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 400,
                    color: '#808080',
                  }}
                >
                  Essa parceria oferece Seguro Garantia que une tecnologia,
                  praticidade e solidez para facilitar sua participação em
                  licitações e contratos públicos com total confiança.
                </Typography>
                <Typography
                  sx={{
                    mt: { xs: '8px', md: '16px' },
                    mb: { xs: '8px', md: '16px' },
                    color: '#209DDB',
                    fontWeight: 700,
                    fontSize: { xs: '12px', md: '14px' },
                  }}
                >
                  O que oferecemos para você:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: '16px',
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 400,
                    color: '#808080',
                  }}
                >
                  <Box
                    component="li"
                    sx={{
                      marginBottom: { xs: '8px', md: '12px' },
                      fontWeight: 700,
                    }}
                  >
                    Seguro Garantia Simplificado
                    <Box component={'p'} sx={{ fontWeight: 400 }}>
                      Solicite o Seguro Garantia para Proposta ou Contrato
                      diretamente aqui, de forma rápida, prática e totalmente
                      integrada — sem burocracia e com aprovação ágil.
                    </Box>
                  </Box>
                  <Box
                    component="li"
                    sx={{
                      marginBottom: { xs: '8px', md: '12px' },
                      fontWeight: 700,
                    }}
                  >
                    Mais segurança e tranquilidade
                    <Box component={'p'} sx={{ fontWeight: 400 }}>
                      Com o respaldo da Asas Garantias e da AllSeg Seguradora,
                      sua empresa participa com a segurança de quem conta com
                      instituições sólidas e reconhecidas no mercado
                    </Box>
                  </Box>
                  <Box
                    component="li"
                    sx={{
                      marginBottom: { xs: '8px', md: '12px' },
                      fontWeight: 700,
                    }}
                  >
                    Condições exclusivas
                    <Box component={'p'} sx={{ fontWeight: 400 }}>
                      Aproveite condições diferenciadas e vantagens exclusivas
                      desta parceria. Tenha mais competitividade, agilidade e
                      confiança em todas as etapas do processo.
                    </Box>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    mt: { xs: '8px', md: '16px' },
                    mb: { xs: '8px', md: '16px' },
                    color: '#808080',
                    fontWeight: 400,
                    fontSize: { xs: '12px', md: '14px' },
                  }}
                >
                  Asas Garantias e AllSeg Seguradora: segurança e eficiência a
                  serviço das contratações públicas.
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  background: '#0a3d91',
                  borderRadius: '13px',
                  p: { xs: '16px', md: '24px' },
                  flexDirection: 'column',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: { xs: '100%', md: '300px' },
                  minHeight: { xs: 'unset', md: '410px' },
                  boxShadow: '0px 4px 4px 0px #00000040',
                }}
              >
                <form
                  onSubmit={handleSubmit}
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <input type="hidden" name="id" value={form.id} />
                  <Typography
                    sx={{
                      mb: { xs: '10px', md: '20px' },
                      fontWeight: 700,
                      fontSize: { xs: '11px', md: '14px' },
                    }}
                  >
                    Preencha os dados abaixo e receba uma avaliação gratuita e
                    descubra como o nosso novo serviço de Seguro Garantia pode
                    transformar sua experiência em licitações e contratos
                    públicos.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: { xs: '6px', md: '16px' },
                      border: '1px solid #fff',
                      borderRadius: 1,
                      p: { xs: '4px', md: '8px' },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: { xs: '12px', md: '14px' },
                        mr: { xs: '4px', md: '8px' },
                      }}
                    >
                      Contato:
                    </Typography>
                    <input
                      name="nome"
                      value={form.nome}
                      onChange={handleChange}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        flex: 1,
                        color: '#fff',
                        padding: '8px 0',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      required
                      disabled={loading}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: { xs: '6px', md: '16px' },
                      border: '1px solid #fff',
                      borderRadius: 1,
                      p: { xs: '4px', md: '8px' },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: { xs: '12px', md: '14px' },
                        mr: { xs: '4px', md: '8px' },
                      }}
                    >
                      CNPJ:
                    </Typography>
                    <input
                      name="cnpj"
                      value={formatCNPJ(form.cnpj)}
                      onChange={handleCNPJChange}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        flex: 1,
                        color: '#fff',
                        padding: '8px 0',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      required
                      disabled={loading}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: { xs: '6px', md: '16px' },
                      border: '1px solid #fff',
                      borderRadius: 1,
                      p: { xs: '4px', md: '8px' },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: { xs: '12px', md: '14px' },
                        mr: { xs: '4px', md: '8px' },
                      }}
                    >
                      E-mail:
                    </Typography>
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        flex: 1,
                        color: '#fff',
                        padding: '8px 0',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      required
                      disabled={loading}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: { xs: '6px', md: '16px' },
                      border: '1px solid #fff',
                      borderRadius: 1,
                      p: { xs: '4px', md: '8px' },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: { xs: '12px', md: '14px' },
                        mr: { xs: '4px', md: '8px' },
                      }}
                    >
                      Telefone:
                    </Typography>
                    <input
                      name="telefone"
                      value={formatPhone(form.telefone)}
                      onChange={handleTelefoneChange}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        flex: 1,
                        color: '#fff',
                        padding: '8px 0',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      required
                      disabled={loading}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    disabled={loading}
                    type="submit"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '12px', md: '14px' },
                      backgroundColor: '#FFF',
                      color: showProgress ? '#FFF' : '#2064b8',
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: '100% !important',
                      transition: 'color 0.2s ease',
                      width: '100%',
                      ':hover': { background: '#FFFFFFE6' },
                      ':disabled': {
                        color: '#FFF',
                        backgroundColor: '#ffffff9d',
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {loading
                        ? 'Processando...'
                        : error
                          ? 'Tentar Novamente'
                          : 'Consulte Agora'}
                    </Box>

                    {loading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: `${timeoutProgress}%`,
                          backgroundColor: '#2064b8',
                          zIndex: 0,
                          transition: 'width 100ms linear',
                        }}
                      />
                    )}
                  </Button>
                  {error && (
                    <Box
                      sx={{
                        width: '100%',
                        mt: '12px',
                        background: '#FDEDED',
                        padding: '4px',
                      }}
                    >
                      <Typography
                        sx={{
                          textAlign: 'center',
                          color: 'red',
                          fontSize: { xs: '10px', md: '12px' },
                        }}
                      >
                        {error}
                      </Typography>
                    </Box>
                  )}
                </form>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
